/**
 * @author Rejo Varghese
 * Pure compute helpers for settlement drafts. Given the bundled workflow
 * payload from /api/settlement/workflowData, produces per-operator activity
 * aggregates + line items + signed totals.
 *
 * No DB access, no side effects — safe to call in the browser.
 *
 * Logic mirrors the existing cost-summary report at
 * src/app/pages/reports/cost-summary.report.js:22-184.
 */

import { isValid, isValidArray, isValidNumber, isValidString } from "./utilities";
import { applyPartnerActivityTotalsToElement } from "./ota-partner.util";

const GST_RATE = 0.18;

// Line codes that belong to the "web tickets" half of a split settlement.
// Everything else falls under "other charges".
const WEB_TICKET_LINE_CODES = [
    "WEB_TICKET_CONFIRMED",
    "WEB_TICKET_CANCELLED",
    "WEB_TICKET_COMMISSION",
    "CANCELLATION_PENALTY"  // collected on behalf of operator on web tickets
];

const DEFAULTS = {
    smsCharge: 0.20,
    whatsappCharge: 0.25,
    gpsCharge: 550,
    copilotTabsCharge: 550,
    monthlyMaintenanceCharge: 1000,
    stdTripCharge: 35,
    ssTripCharge: 100,
    otaPercent: 1,
    websiteCommissionPct: 4,        // GST-inclusive default; gets divided by 1.18 below
    qwikbusOtaCommissionPct: 10,    // 10% on OTA partner confirmed fare
    qwikbusOtaApiPct: 2,            // 2% API charge on OTA partner confirmed fare
    mavenCancellationPenaltyShare: 0.5  // we keep 50% of partner cancellation penalties as credit
};

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Pull per-operator config values, falling back to defaults.
 */
function resolveOperatorConfig(operator) {
    const tac = operator.travels_app_config || {};
    const cfg = { ...DEFAULTS };

    if (isValid(tac.MONTHLY_GPS_CHARGE)) cfg.gpsCharge = num(tac.MONTHLY_GPS_CHARGE);
    if (isValid(tac.MONTHLY_COPILOT_CHARGE)) cfg.copilotTabsCharge = num(tac.MONTHLY_COPILOT_CHARGE);
    if (isValidNumber(tac.SMS_CHARGE)) cfg.smsCharge = num(tac.SMS_CHARGE);
    if (isValidNumber(tac.WHATSAPP_CHARGE)) cfg.whatsappCharge = num(tac.WHATSAPP_CHARGE);
    if (isValidNumber(tac.STD_TRIP_CHARGE)) cfg.stdTripCharge = num(tac.STD_TRIP_CHARGE);
    if (isValidNumber(tac.SEAT_SHARING_TRIP_CHARGE)) cfg.ssTripCharge = num(tac.SEAT_SHARING_TRIP_CHARGE);
    if (isValidNumber(tac.OTA_PERCENT)) cfg.otaPercent = num(tac.OTA_PERCENT);
    if (isValidNumber(tac.MONTHLY_MAINTENANCE_CHARGE)) cfg.monthlyMaintenanceCharge = num(tac.MONTHLY_MAINTENANCE_CHARGE);

    cfg.billingType = isValidString(tac.BILLING_TYPE) ? tac.BILLING_TYPE : "Trip Based";
    cfg.gpsDeviceCount = num(tac.GPS_DEVICE_NUM);
    // CoPilot device count is no longer read from COPILOT_TABS — it's computed
    // server-side as min(driver-app subscriptions, buses) and arrives via the
    // workflow activity (see normalizeActivity.copilotDeviceCount).

    if (operator.website_agent && isValid(operator.website_agent.commission_percentage)) {
        const raw = num(operator.website_agent.commission_percentage);
        // Stored value is GST-inclusive; convert to GST-exclusive for the line.
        cfg.websiteCommissionPct = raw / (1 + GST_RATE);
    }
    return cfg;
}

/**
 * Convert one row from the travels-server's getWorkflowReport response into the
 * field-name shape our line builder expects.
 *
 * The travels-server `getWorkflowReport` already aggregates trips/tickets/sms
 * per operator and applies the 50% Maven cancellation-penalty share.
 *
 * Field-name notes (preserving travels-server's typos):
 *   workflowSummary.cancellationPenality       (typo)  -> cancellationPenalty
 *   workflowSummary.mavenCancellationPenality  (typo, already 50% applied)
 *                                                      -> mavenCancellationPenaltyShare
 *   workflowSummary.whatsAppSmsCount                   -> whatsappCount
 */
function normalizeActivity(summary) {
    if (!summary) {
        return {
            serviceCounts: 0, seatSharingServiceCount: 0,
            confirmedAmount: 0, cancelledAmount: 0, cancellationPenalty: 0,
            mavenConfirmedFare: 0, mavenCancellationPenaltyShare: 0,
            otaConfirmedFare: 0, smsCount: 0, whatsappCount: 0,
            copilotDeviceCount: 0
        };
    }
    // Settlements always show the WHOLE OTA partner program - maven plus
    // every registry partner - as QwikBus: fold partnerActivity into the
    // maven aggregate fields here. Idempotent, so data already folded by
    // getWorkFlowAction is not double-counted.
    applyPartnerActivityTotalsToElement(summary);
    return {
        serviceCounts: num(summary.serviceCounts),
        seatSharingServiceCount: num(summary.seatSharingServiceCount),
        confirmedAmount: num(summary.confirmedAmount),
        cancelledAmount: num(summary.cancelledAmount),
        cancellationPenalty: num(summary.cancellationPenality),
        mavenConfirmedFare: num(summary.mavenConfirmedFare),
        mavenCancellationPenaltyShare: num(summary.mavenCancellationPenality),
        otaConfirmedFare: num(summary.otaConfirmedFare),
        smsCount: num(summary.smsCount),
        whatsappCount: num(summary.whatsAppSmsCount),
        // Server-computed: min(driver-app subscriptions, buses) — see report_service.js
        copilotDeviceCount: num(summary.copilotDeviceCount)
    };
}

/**
 * For WEEKLY cycles, prorate the monthly maintenance charge by
 * (period days / days in calendar month containing period_end).
 * Returns 1 for MONTHLY (full charge).
 */
function computeMaintenanceProrationFactor(cycle, periodStart, periodEnd) {
    if (cycle !== "WEEKLY") return 1;
    if (!periodStart || !periodEnd) return 1;
    const start = new Date(`${periodStart}T00:00:00Z`);
    const end = new Date(`${periodEnd}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
    const periodDays = Math.round((end - start) / 86400000) + 1;
    // Days in the calendar month containing period_end
    const daysInMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)).getUTCDate();
    if (daysInMonth <= 0) return 1;
    return periodDays / daysInMonth;
}

/**
 * Build the line items for a single operator. Returns a flat array of
 * { line_code, description, quantity, unit_price, charge_amount, credit_amount, sort_order }.
 *
 * Mirrors the line shape stored in operator_settlement_lines.
 *
 * periodCtx: { cycle, periodStart, periodEnd } — used to prorate monthly
 *            maintenance for WEEKLY cycles.
 */
function buildLines(cfg, activity, periodCtx) {
    const cycle = (periodCtx && periodCtx.cycle) || "MONTHLY";
    const periodStart = periodCtx && periodCtx.periodStart;
    const periodEnd = periodCtx && periodCtx.periodEnd;
    const proration = computeMaintenanceProrationFactor(cycle, periodStart, periodEnd);
    const isProrated = cycle === "WEEKLY" && proration < 1;
    // Apply proration to ALL monthly charges (maintenance, CoPilot, GPS).
    // Per-operator rates come from travels_app_config:
    //   MONTHLY_MAINTENANCE_CHARGE, MONTHLY_COPILOT_CHARGE, MONTHLY_GPS_CHARGE
    const proratedMaintenance = cfg.monthlyMaintenanceCharge * proration;
    const proratedCopilotRate = cfg.copilotTabsCharge * proration;
    const proratedGpsRate = cfg.gpsCharge * proration;
    // Display factor as a 2-decimal number (e.g. 0.23 for 7/30)
    const factorDisplay = Math.round(proration * 100) / 100;
    const lines = [];
    let ord = 0;
    const push = (line) => lines.push({ ...line, sort_order: ord++ });

    // Web ticket sales (charge: % commission on confirmed+cancelled value; credit: confirmed amount)
    const webTicketBase = activity.confirmedAmount + activity.cancelledAmount;
    if (webTicketBase > 0 || activity.confirmedAmount > 0) {
        push({
            line_code: "WEB_TICKET_CONFIRMED",
            description: "Confirmed web ticket amount",
            quantity: round2(activity.confirmedAmount),
            unit_price: null,
            unit_is_percent: 0,
            auto_compute: 0,
            charge_amount: 0,
            credit_amount: round2(activity.confirmedAmount)
        });
    }
    // NOTE: the cancelled ticket value is NOT credited to the operator — that
    // amount is refunded to the passenger. Only the cancellation penalty
    // (collected on the operator's behalf) is a credit; see CANCELLATION_PENALTY below.
    if (webTicketBase > 0) {
        push({
            line_code: "WEB_TICKET_COMMISSION",
            description: `Charges for web ticket sales (${round2(cfg.websiteCommissionPct)}%)`,
            quantity: round2(webTicketBase),
            unit_price: round2(cfg.websiteCommissionPct),
            unit_is_percent: 1,
            auto_compute: 1,
            charge_amount: round2(webTicketBase * cfg.websiteCommissionPct / 100),
            credit_amount: 0
        });
    }
    if (activity.cancellationPenalty > 0) {
        push({
            line_code: "CANCELLATION_PENALTY",
            description: "Web ticket cancellation penalty (collected from passenger)",
            quantity: round2(activity.cancellationPenalty),
            unit_price: null,
            unit_is_percent: 0,
            auto_compute: 0,
            charge_amount: 0,
            credit_amount: round2(activity.cancellationPenalty)
        });
    }

    // ----- Monthly Maintenance Charge (prorated for weekly cycle) -----
    {
        const monthlyDescription = isProrated
            ? `Monthly maintenance charge (₹${cfg.monthlyMaintenanceCharge}/month, prorated × ${factorDisplay})`
            : `Monthly maintenance charge (₹${cfg.monthlyMaintenanceCharge}/month)`;
        push({
            line_code: "MONTHLY_MAINTENANCE",
            description: monthlyDescription,
            quantity: 1,
            unit_price: round2(proratedMaintenance),
            unit_is_percent: 0,
            auto_compute: 1,
            charge_amount: round2(proratedMaintenance),
            credit_amount: 0
        });
    }

    // ----- Trip-based charges (only when billingType = Trip Based) -----
    // Combined std + SS into one row per the requested 2-row layout.
    // auto_compute is OFF because std and SS have different rates so it isn't a clean qty*unit.
    if (cfg.billingType === "Trip Based") {
        const stdTripCount = Math.max(activity.serviceCounts - activity.seatSharingServiceCount, 0);
        const tripCharge = (stdTripCount * cfg.stdTripCharge)
            + (activity.seatSharingServiceCount * cfg.ssTripCharge);
        push({
            line_code: "TRIP_CHARGE",
            description:
                `Trip-based charges (${stdTripCount} std @ ${cfg.stdTripCharge}` +
                (activity.seatSharingServiceCount > 0
                    ? ` + ${activity.seatSharingServiceCount} SS @ ${cfg.ssTripCharge})`
                    : `)`),
            quantity: activity.serviceCounts,
            unit_price: null,
            unit_is_percent: 0,
            auto_compute: 0,
            charge_amount: round2(tripCharge),
            credit_amount: 0
        });
    }

    // SMS usage
    push({
        line_code: "SMS_USAGE",
        description: `SMS cost based on usage (${cfg.smsCharge} per SMS)`,
        quantity: round2(activity.smsCount),
        unit_price: cfg.smsCharge,
        unit_is_percent: 0,
        auto_compute: 1,
        charge_amount: round2(activity.smsCount * cfg.smsCharge),
        credit_amount: 0
    });

    // WhatsApp usage (only if non-zero)
    if (activity.whatsappCount > 0) {
        push({
            line_code: "WHATSAPP_USAGE",
            description: `WhatsApp cost based on usage (${cfg.whatsappCharge} per msg)`,
            quantity: round2(activity.whatsappCount),
            unit_price: cfg.whatsappCharge,
            unit_is_percent: 0,
            auto_compute: 1,
            charge_amount: round2(activity.whatsappCount * cfg.whatsappCharge),
            credit_amount: 0
        });
    }

    // QwikBus (Maventech) booked ticket value — credited to operator
    if (activity.mavenConfirmedFare > 0) {
        push({
            line_code: "OTA_TICKET_CONFIRMED",
            description: "QwikBus booked ticket amount",
            quantity: round2(activity.mavenConfirmedFare),
            unit_price: null,
            unit_is_percent: 0,
            auto_compute: 0,
            charge_amount: 0,
            credit_amount: round2(activity.mavenConfirmedFare)
        });
    }

    // QwikBus OTA commission (10%) on maven confirmed fare
    push({
        line_code: "OTA_COMMISSION",
        description: `QwikBus online agent commission (${cfg.qwikbusOtaCommissionPct}%)`,
        quantity: round2(activity.mavenConfirmedFare),
        unit_price: cfg.qwikbusOtaCommissionPct,
        unit_is_percent: 1,
        auto_compute: 1,
        charge_amount: round2(activity.mavenConfirmedFare * cfg.qwikbusOtaCommissionPct / 100),
        credit_amount: 0
    });

    // 3rd-party API charge (2%) on maven confirmed fare
    push({
        line_code: "OTA_API_CHARGE",
        description: `API charges for 3rd-party online agents (${cfg.qwikbusOtaApiPct}%)`,
        quantity: round2(activity.mavenConfirmedFare),
        unit_price: cfg.qwikbusOtaApiPct,
        unit_is_percent: 1,
        auto_compute: 1,
        charge_amount: round2(activity.mavenConfirmedFare * cfg.qwikbusOtaApiPct / 100),
        credit_amount: 0
    });

    if (activity.mavenCancellationPenaltyShare > 0) {
        push({
            line_code: "OTA_CANCELLATION_PENALTY",
            description: "QwikBus online agents - cancellation penalty (50% share)",
            quantity: round2(activity.mavenCancellationPenaltyShare),
            unit_price: null,
            unit_is_percent: 0,
            auto_compute: 0,
            charge_amount: 0,
            credit_amount: round2(activity.mavenCancellationPenaltyShare)
        });
    }

    // OTA-based billing: always include OTA booking charge line when NOT Trip Based,
    // even at zero, so admin can edit it if needed.
    if (cfg.billingType !== "Trip Based") {
        push({
            line_code: "OTA_BOOKING_CHARGE",
            description: `OTA booking charges (${cfg.otaPercent}%)`,
            quantity: round2(activity.otaConfirmedFare),
            unit_price: cfg.otaPercent,
            unit_is_percent: 1,
            auto_compute: 1,
            charge_amount: round2(activity.otaConfirmedFare * cfg.otaPercent / 100),
            credit_amount: 0
        });
    }

    // CoPilot maintenance charge — prorated for WEEKLY cycle.
    // Device count is server-computed as min(driver-app subscriptions, buses),
    // not the static COPILOT_TABS config.
    const copilotDevices = num(activity.copilotDeviceCount);
    if (copilotDevices > 0) {
        const copilotDesc = isProrated
            ? `CoPilot app maintenance charge (₹${cfg.copilotTabsCharge}/month/bus, prorated × ${factorDisplay})`
            : `CoPilot app maintenance charge (₹${cfg.copilotTabsCharge}/month/bus)`;
        push({
            line_code: "COPILOT_MAINT",
            description: copilotDesc,
            quantity: copilotDevices,
            unit_price: round2(proratedCopilotRate),
            unit_is_percent: 0,
            auto_compute: 1,
            charge_amount: round2(copilotDevices * proratedCopilotRate),
            credit_amount: 0
        });
    }

    // GPS monthly charge — prorated for WEEKLY cycle
    if (cfg.gpsDeviceCount > 0) {
        const gpsDesc = isProrated
            ? `GPS monthly platform charges (₹${cfg.gpsCharge}/device, prorated × ${factorDisplay})`
            : `GPS monthly platform charges (₹${cfg.gpsCharge}/device)`;
        push({
            line_code: "GPS_MONTHLY",
            description: gpsDesc,
            quantity: cfg.gpsDeviceCount,
            unit_price: round2(proratedGpsRate),
            unit_is_percent: 0,
            auto_compute: 1,
            charge_amount: round2(cfg.gpsDeviceCount * proratedGpsRate),
            credit_amount: 0
        });
    }
    return lines;
}

/**
 * Compute totals for an array of line items + opening balance.
 * Returns: { subTotalCharges, subTotalCredits, gstAmount, invoiceAmount, netSettlement }.
 * Note: `invoiceAmount` is the legacy field name kept for compatibility with the
 * server payload; the UI labels this as "Settlement Amount".
 */
export function computeTotals(lines, openingBalance) {
    const subTotalCharges = (lines || []).reduce((s, l) => s + num(l.charge_amount), 0);
    const subTotalCredits = (lines || []).reduce((s, l) => s + num(l.credit_amount), 0);
    const gstAmount = subTotalCharges * GST_RATE;
    const invoiceAmount = subTotalCharges + gstAmount;
    const ob = num(openingBalance);
    const netSettlement = subTotalCredits - invoiceAmount + ob;
    return {
        subTotalCharges: round2(subTotalCharges),
        subTotalCredits: round2(subTotalCredits),
        gstAmount: round2(gstAmount),
        invoiceAmount: round2(invoiceAmount),
        netSettlement: round2(netSettlement)
    };
}

/**
 * Top-level entry. Given the workflow payload + the cycle for this run,
 * returns one entry per active operator with { operator, lines, totals,
 * openingBalance, activity }.
 *
 * cycle ('WEEKLY' | 'MONTHLY') drives proration of the monthly maintenance
 * charge — for WEEKLY cycles the charge is multiplied by
 * (period_days / days_in_calendar_month_of_period_end).
 */
/**
 * Returns array of 'FULL' | 'WEB_ONLY' | 'OTHER_ONLY' kinds for an operator.
 * Split-enabled operators always emit both halves as separate rows.
 */
function decideKinds(opCfg) {
    const isSplit = opCfg && (
        opCfg.SETTLEMENT_SEPARATE_OTHER_CHARGES === "1" ||
        opCfg.SETTLEMENT_SEPARATE_OTHER_CHARGES === 1
    );
    if (!isSplit) return ["FULL"];
    return ["WEB_ONLY", "OTHER_ONLY"];
}

function partitionLinesByKind(lines, kind) {
    if (kind === "FULL") return lines;
    if (kind === "WEB_ONLY") {
        return lines.filter((l) => WEB_TICKET_LINE_CODES.indexOf(l.line_code) !== -1);
    }
    // OTHER_ONLY
    return lines.filter((l) => WEB_TICKET_LINE_CODES.indexOf(l.line_code) === -1);
}

const KIND_LABELS = {
    FULL: "Full",
    WEB_ONLY: "Web Tickets",
    OTHER_ONLY: "Other Charges"
};

/**
 * Top-level builder.
 *
 * workflow shape (now stitched on the client from 3 separate fetches):
 *   {
 *     period: { start, end },                  // from /api/settlement/workflowData (extras)
 *     financialYear: {...},                    // from extras
 *     openingBalances: [...],                  // from extras
 *     priorOutstandings: { [opCode]: {...} },  // from extras
 *     operators: [...],                        // from travels: getActiveOperatorsForSettlement
 *     workflowSummary: [...]                   // from travels: getWorkflowReport (pre-aggregated)
 *   }
 */
export function buildOperatorDrafts(workflow, cycle) {
    if (!workflow || !isValidArray(workflow.operators)) return [];
    const openingByOp = {};
    if (isValidArray(workflow.openingBalances)) {
        workflow.openingBalances.forEach((ob) => { openingByOp[ob.operator_code] = ob; });
    }
    // priorOutstandings: per-operator carry-forward from the most-recent
    // unsettled prior settlement. Server-computed signed amount.
    const priorByOp = (workflow && workflow.priorOutstandings) || {};

    // Index workflow summary rows by operator_code for O(1) per-operator lookup.
    // Travels server uses `operatorCode` (camelCase) as the join key in workflow report rows.
    const summaryByOp = {};
    if (isValidArray(workflow.workflowSummary)) {
        workflow.workflowSummary.forEach((row) => {
            if (row && row.operatorCode) summaryByOp[row.operatorCode] = row;
        });
    }

    const periodCtx = {
        cycle: cycle || "MONTHLY",
        periodStart: workflow.period && workflow.period.start,
        periodEnd: workflow.period && workflow.period.end
    };

    const out = [];
    workflow.operators.forEach((operator) => {
        const cfg = resolveOperatorConfig(operator);
        const activity = normalizeActivity(summaryByOp[operator.operator_code]);
        const allLines = buildLines(cfg, activity, periodCtx);

        // Opening balance precedence:
        //   1. Prior unsettled settlement's signed outstanding (carry-forward)
        //   2. FY-scoped operator_opening_balances seed
        //   3. 0
        // Note: for split settlements we apply the prior outstanding only to the
        // FIRST emitted kind so the carry-forward isn't double-counted.
        let openingBalance = 0;
        let openingSource = null;
        const prior = priorByOp[operator.operator_code];
        if (prior && prior.signed_outstanding != null && Number(prior.signed_outstanding) !== 0) {
            openingBalance = num(prior.signed_outstanding);
            openingSource = {
                kind: "PRIOR_SETTLEMENT",
                settlement_no: prior.settlement_no,
                period: prior.period_start && prior.period_end
                    ? `${prior.period_start} → ${prior.period_end}` : null
            };
        } else if (openingByOp[operator.operator_code]) {
            openingBalance = num(openingByOp[operator.operator_code].amount);
            openingSource = { kind: "FY_SEED", financial_year: openingByOp[operator.operator_code].financial_year };
        }

        const kinds = decideKinds(operator.travels_app_config);
        kinds.forEach((kind, idx) => {
            const lines = partitionLinesByKind(allLines, kind);
            // First emitted kind gets the opening-balance carry-forward; subsequent kinds start at 0
            const ob = idx === 0 ? openingBalance : 0;
            const obSrc = idx === 0 ? openingSource : null;
            const totals = computeTotals(lines, ob);
            out.push({
                operator,
                kind,
                kindLabel: KIND_LABELS[kind],
                isSplit: kinds.length > 1 || kind !== "FULL",
                cycle_config: {
                    cycle: operator.travels_app_config && operator.travels_app_config.SETTLEMENT_CYCLE,
                    weekday: operator.travels_app_config && operator.travels_app_config.SETTLEMENT_WEEKDAY,
                    monthDay: operator.travels_app_config && operator.travels_app_config.SETTLEMENT_MONTH_DAY,
                    enabled: operator.travels_app_config && operator.travels_app_config.SETTLEMENT_ENABLED == 1,
                    splitWebOther: operator.travels_app_config && (
                        operator.travels_app_config.SETTLEMENT_SEPARATE_OTHER_CHARGES === "1" ||
                        operator.travels_app_config.SETTLEMENT_SEPARATE_OTHER_CHARGES === 1
                    )
                },
                activity,
                openingBalance: ob,
                openingSource: obSrc,
                lines,
                totals
            });
        });
    });
    return out;
}
